/**
 * PropertyDataReview Component
 *
 * Step 2 of the property video wizard: Review scraped property data.
 * Supports optional Nearby Places section for draft property flow.
 */

import { Building, MapPin, Square, ImageIcon, FileText, Tag, MapPinned } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import type { IPropertyDataReviewProps, INearbyPlaces } from '@/@types/property';
import {
  BasicInfoSection,
  SpecificationsSection,
  LocationSection,
  FeaturesSection,
  DescriptionSection,
  ImagesSection,
} from './PropertyDataReviewHelpers';

// ============================================================================
// NearbyPlacesSection Helper Component
// ============================================================================

interface INearbyPlacesSectionProps {
  nearbyPlaces?: INearbyPlaces;
}

/**
 * Displays nearby places in a grid layout for the combined "Property Data" step
 */
function NearbyPlacesSection({ nearbyPlaces }: INearbyPlacesSectionProps) {
  if (!nearbyPlaces) {
    return <p className="text-sm text-muted-foreground">No nearby places data available.</p>;
  }

  const categories = [
    { key: 'schools' as const, label: 'Schools' },
    { key: 'kindergartens' as const, label: 'Kindergartens' },
    { key: 'supermarkets' as const, label: 'Supermarkets' },
    { key: 'restaurants' as const, label: 'Restaurants' },
    { key: 'busStops' as const, label: 'Bus Stops' },
    { key: 'pharmacies' as const, label: 'Pharmacies' },
  ];

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {categories.map(({ key, label }) => {
        const places = nearbyPlaces[key] || [];
        return (
          <div key={key} className="space-y-1">
            <span className="text-sm font-medium">{label}</span>
            {places.length > 0 ? (
              <ul className="text-sm text-muted-foreground space-y-0.5">
                {places.slice(0, 3).map(place => (
                  <li key={`${place.name}-${place.distance}`}>
                    {place.name} ({Math.round(place.distance)}m)
                  </li>
                ))}
                {places.length > 3 && <li className="text-xs">+{places.length - 3} more</li>}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">None found</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Displays all scraped property data organized in accordion sections.
 */
function PropertyDataReview({
  property,
  onEdit: _onEdit,
  showNearbyPlaces = false,
}: IPropertyDataReviewProps) {
  const hasFeatures = property.features && property.features.length > 0;
  const hasImages = property.imageIds && property.imageIds.length > 0;

  return (
    <Card className="w-full max-w-3xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building className="h-5 w-5" aria-hidden="true" />
          Property Data Review
        </CardTitle>
        <CardDescription>
          Review the scraped property data below. You can proceed to the next step when ready.
        </CardDescription>
      </CardHeader>

      <CardContent>
        <Accordion
          type="multiple"
          defaultValue={['basic', 'specs', 'location', 'features', 'description', 'images']}
          className="w-full"
        >
          <AccordionItem value="basic">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <Building className="h-4 w-4" aria-hidden="true" />
                Basic Information
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <BasicInfoSection
                title={property.title}
                propertyType={property.propertyType}
                price={property.price}
                currency={property.currency}
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="specs">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <Square className="h-4 w-4" aria-hidden="true" />
                Specifications
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <SpecificationsSection
                bedrooms={property.bedrooms}
                bathrooms={property.bathrooms}
                squareMeters={property.squareMeters}
                yearBuilt={property.yearBuilt}
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="location">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <MapPin className="h-4 w-4" aria-hidden="true" />
                Location
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <LocationSection
                address={property.address}
                city={property.city}
                coordinates={property.coordinates}
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="features">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <Tag className="h-4 w-4" aria-hidden="true" />
                Features
                {hasFeatures && (
                  <Badge variant="secondary" className="ml-2">
                    {property.features?.length}
                  </Badge>
                )}
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <FeaturesSection features={property.features} />
            </AccordionContent>
          </AccordionItem>

          {showNearbyPlaces && (
            <AccordionItem value="nearbyPlaces">
              <AccordionTrigger>
                <span className="flex items-center gap-2">
                  <MapPinned className="h-4 w-4" aria-hidden="true" />
                  Nearby Places
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <NearbyPlacesSection nearbyPlaces={property.nearbyPlaces} />
              </AccordionContent>
            </AccordionItem>
          )}

          <AccordionItem value="description">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4" aria-hidden="true" />
                AI-Generated Description
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <DescriptionSection description={property.generatedDescription} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="images">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4" aria-hidden="true" />
                Property Images
                {hasImages && (
                  <Badge variant="secondary" className="ml-2">
                    {property.imageIds.length}
                  </Badge>
                )}
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <ImagesSection imageIds={property.imageIds} propertyId={property._id} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
}

export { PropertyDataReview };
